import { useCallback } from 'react';
import { canvasToImage } from '../utils';
import { getPrintPaginationStyles } from '../utils/print-pagination.ts';

const PRINT_CLONE_ID = 'ui-inline-print-clone';
const PRINT_STYLE_ID = 'ui-inline-print-style';

export const usePrint = (printAreaId = 'print-area', showAlertSettingPrint = true) => {
  const handlePrint = useCallback(() => {
    const source = document.getElementById(printAreaId);
    if (!source) return;

    const clone = source.cloneNode(true) as HTMLElement;
    clone.id = PRINT_CLONE_ID;

    document.getElementById(PRINT_CLONE_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();

    const originalCanvases = source.querySelectorAll('canvas');
    const clonedCanvases = clone.querySelectorAll('canvas');
    const originalSelects = source.querySelectorAll('select');
    const clonedSelects = clone.querySelectorAll('select');

    originalCanvases.forEach((originalCanvas, index) => {
      const clonedCanvas = clonedCanvases[index];
      if (!clonedCanvas) return;
      const img = canvasToImage(originalCanvas);
      clonedCanvas.replaceWith(img);
    });

    originalSelects.forEach((originalSelect, index) => {
      const clonedSelect = clonedSelects[index] as HTMLSelectElement | undefined;
      if (!clonedSelect) return;

      const value = (originalSelect as HTMLSelectElement).value;
      clonedSelect.value = value;

      Array.from(clonedSelect.options).forEach(option => {
        option.selected = option.value === value;
      });
    });

    const originalInputs = source.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    const clonedInputs = clone.querySelectorAll('input[type="checkbox"], input[type="radio"]');

    originalInputs.forEach((originalInput, index) => {
      const clonedInput = clonedInputs[index] as HTMLInputElement | undefined;
      if (!clonedInput) return;

      clonedInput.checked = (originalInput as HTMLInputElement).checked;
      clonedInput.defaultChecked = (originalInput as HTMLInputElement).checked;
    });

    clone.querySelectorAll('*').forEach(el => {
      if (el.tagName === 'IMG' || el.tagName === 'SVG' || el.tagName === 'CANVAS') {
        return;
      }

      const htmlEl = el as HTMLElement;
      htmlEl.style.overflow = 'visible';
      htmlEl.style.maxHeight = 'none';
      if (htmlEl.style.height && htmlEl.style.height !== 'auto') {
        htmlEl.style.height = 'auto';
      }
    });

    const style = document.createElement('style');
    style.id = PRINT_STYLE_ID;
    style.textContent = `
      @media print {
        html,
        body {
          width: 100% !important;
          height: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body > * {
          display: none !important;
        }

        body > #${PRINT_CLONE_ID} {
          display: block !important;
          position: static !important;
          inset: auto !important;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
          background: #fff !important;
          color: inherit !important;
          box-sizing: border-box !important;
        }

        body > #${PRINT_CLONE_ID} * {
          visibility: visible !important;
          max-height: none !important;
          overflow: visible !important;
        }

        body > #${PRINT_CLONE_ID} div,
        body > #${PRINT_CLONE_ID} section,
        body > #${PRINT_CLONE_ID} article,
        body > #${PRINT_CLONE_ID} table,
        body > #${PRINT_CLONE_ID} tr,
        body > #${PRINT_CLONE_ID} td,
        body > #${PRINT_CLONE_ID} th,
        body > #${PRINT_CLONE_ID} p {
          break-inside: auto;
          page-break-inside: auto;
        }

        body > #${PRINT_CLONE_ID} .custom-control-label::before,
        body > #${PRINT_CLONE_ID} .custom-control-label::after {
          display: none !important;
          content: none !important;
        }

        body > #${PRINT_CLONE_ID} .custom-control-label {
          padding-left: 0 !important;
          margin-left: 0 !important;
        }

        body > #${PRINT_CLONE_ID} img,
        body > #${PRINT_CLONE_ID} canvas {
          max-width: 100% !important;
          height: auto !important;
          object-fit: contain !important;
        }

        body > #${PRINT_CLONE_ID} thead {
          display: table-header-group !important;
        }
      }
      ${getPrintPaginationStyles(`body > #${PRINT_CLONE_ID}`)}
    `;

    document.body.appendChild(clone);
    document.head.appendChild(style);

    const waitForImages = Promise.all(
      Array.from(clone.querySelectorAll('img')).map(
        img =>
          new Promise<void>(resolve => {
            const htmlImg = img as HTMLImageElement;

            if (htmlImg.complete && htmlImg.naturalWidth > 0) {
              resolve();
              return;
            }

            const done = () => resolve();
            htmlImg.addEventListener('load', done, { once: true });
            htmlImg.addEventListener('error', done, { once: true });
          }),
      ),
    );

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      clone.remove();
      style.remove();
    };

    window.addEventListener('afterprint', cleanup, { once: true });

    void Promise.all([waitForImages, document.fonts.ready]).then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.print();
          setTimeout(cleanup, 1500);
        });
      });
    });
  }, [printAreaId, showAlertSettingPrint]);

  return { print: handlePrint };
};
